'use client';

import * as React from 'react';
import gsap from 'gsap';
import { useTranslations } from 'next-intl';
import Container from '@/components/ui/Container/Container';
import CmsImage from '@/components/ui/CmsImage';
import { cn } from '@/components/ui/utils';
import { MEDIA_PLACEHOLDER } from '@/lib/media-placeholder';
import type { SustainabilitySocialFocusPanel } from '@/types/sustainability';

const AUTO_ADVANCE_SEC = 5;

type FocusPanel = {
  id: string;
  title: string;
  body: string;
  bodyHtml?: string;
  imageSrc?: string | null;
  imageAlt: string;
  mobileImageSrc?: string | null;
  mobileImageAlt?: string;
};

export interface SustainabilitySocialFocusSequenceProps {
  cmsIntro?: { title: string; bodyHtml: string } | null;
  cmsPanels?: SustainabilitySocialFocusPanel[];
}

function formatPanelIndex(index: number, total: number): string {
  return `${String(index + 1).padStart(2, '0')} / ${String(total).padStart(2, '0')}`;
}

function verticalPanelLabel(title: string): string {
  return title.toUpperCase();
}

export default function SustainabilitySocialFocusSequence({
  cmsIntro,
  cmsPanels,
}: SustainabilitySocialFocusSequenceProps = {}) {
  const t = useTranslations('sustainability.pages.social');
  const rootRef = React.useRef<HTMLDivElement>(null);
  const progressTween = React.useRef<gsap.core.Tween | null>(null);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const [isInView, setIsInView] = React.useState(false);
  const [reduceMotion, setReduceMotion] = React.useState(false);

  const imagePanels: FocusPanel[] = React.useMemo(() => {
    if (cmsPanels && cmsPanels.length > 0) {
      return cmsPanels
        .filter((panel) => Boolean(panel.imageUrl?.trim()))
        .map((panel) => ({
          id: panel.id,
          title: panel.title,
          body: '',
          bodyHtml: panel.bodyHtml,
          imageSrc: panel.imageUrl,
          imageAlt: panel.imageAlt || panel.title,
          mobileImageSrc: panel.mobileImageUrl,
          mobileImageAlt: panel.mobileImageAlt || panel.imageAlt || panel.title,
        }));
    }

    return [
      {
        id: 'focus-safety',
        title: t('focusSafetyTitle'),
        body: t('focusSafetyBody'),
        imageAlt: t('focusSafetyAlt'),
      },
      {
        id: 'focus-talent',
        title: t('focusTalentTitle'),
        body: t('focusTalentBody'),
        imageAlt: t('focusTalentAlt'),
      },
      {
        id: 'focus-value',
        title: t('focusValueTitle'),
        body: t('focusValueBody'),
        imageAlt: t('focusValueAlt'),
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cmsPanels, t]);

  const introTitle = cmsIntro?.title || t('focusAreasTitle');
  const introBodyHtml = cmsIntro?.bodyHtml;
  const introBody = t('focusAreasIntro');
  const showIntro = Boolean(cmsIntro?.title || cmsIntro?.bodyHtml || !cmsPanels?.length);

  const goToNext = React.useCallback(() => {
    setActiveIndex((current) => (current + 1) % imagePanels.length);
  }, [imagePanels.length]);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [imagePanels]);

  React.useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  React.useEffect(() => {
    const el = rootRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsInView(entry.isIntersecting);
      },
      { threshold: 0.35 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  React.useEffect(() => {
    if (!isInView || imagePanels.length <= 1 || reduceMotion) {
      progressTween.current?.kill();
      return;
    }

    const progressEl = rootRef.current?.querySelector<HTMLElement>('[data-focus-progress]');
    if (!progressEl) return;

    progressTween.current?.kill();
    gsap.set(progressEl, { width: '0%' });
    progressTween.current = gsap.to(progressEl, {
      width: '100%',
      duration: AUTO_ADVANCE_SEC,
      ease: 'none',
      onComplete: goToNext,
    });

    return () => {
      progressTween.current?.kill();
    };
  }, [activeIndex, goToNext, imagePanels.length, isInView, reduceMotion]);

  const renderPanelBody = (panel: FocusPanel, isActive: boolean) =>
    panel.bodyHtml ? (
      <div
        className={cn(
          'mt-3 max-w-xl text-pretty text-sm leading-relaxed text-white/90 transition-all duration-500 sm:mt-4 sm:text-[0.95rem] md:text-base [&_p]:m-0 [&_strong]:font-semibold [&_strong]:text-white',
          isActive ? 'block opacity-100' : 'hidden',
        )}
        dangerouslySetInnerHTML={{ __html: panel.bodyHtml }}
      />
    ) : (
      <p
        className={cn(
          'mt-3 max-w-xl text-pretty text-sm leading-relaxed text-white/90 transition-all duration-500 sm:mt-4 sm:text-[0.95rem] md:text-base',
          isActive ? 'block opacity-100' : 'hidden',
        )}
      >
        {panel.body}
      </p>
    );

  return (
    <div className="bg-black">
      {showIntro ? (
        <Container className="pb-6 sm:pb-8 lg:pb-8 pt-10 sm:pt-12 lg:pt-14">
          <div className="mx-auto max-w-3xl text-center">
            <h2 className="text-3xl font-bold leading-tight tracking-tight text-white sm:text-4xl md:text-[2.75rem]">
              {introTitle}
            </h2>
            {introBodyHtml ? (
              <div
                className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-[0.95rem] md:text-base [&_p]:m-0"
                dangerouslySetInnerHTML={{ __html: introBodyHtml }}
              />
            ) : (
              <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/75 sm:text-[0.95rem] md:text-base">
                {introBody}
              </p>
            )}
          </div>
        </Container>
      ) : null}

      <div ref={rootRef}>
        <Container className="py-4 sm:py-5 lg:py-6 xl:py-8">
          <div className="overflow-hidden rounded-2xl">
            <div className="flex flex-col gap-2.5 sm:gap-3 lg:h-[min(80vh,720px)] lg:min-h-0 lg:flex-row">
              {imagePanels.map((panel, index) => {
                const isActive = index === activeIndex;

                return (
                  <button
                    key={panel.id}
                    type="button"
                    onClick={() => setActiveIndex(index)}
                    className={cn(
                      'group relative w-full overflow-hidden rounded-xl border text-left outline-none transition-[min-height,flex,opacity,transform,border-color] duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] focus-visible:ring-2 focus-visible:ring-[#ed1c22] focus-visible:ring-offset-2 focus-visible:ring-offset-black sm:rounded-2xl',
                      isActive
                        ? 'flex min-h-[min(68svh,500px)] items-end border-white/20 opacity-100 shadow-2xl shadow-black/40 sm:min-h-[430px] md:min-h-[460px] lg:min-h-0 lg:flex-[3.35]'
                        : 'min-h-14 border-white/10 opacity-80 hover:border-white/25 hover:opacity-95 sm:min-h-[3.75rem] lg:min-h-0 lg:flex-[0_0_4.75rem] xl:flex-[0_0_5rem]',
                    )}
                    aria-expanded={isActive}
                    aria-label={`Show ${panel.title}`}
                  >
                    <span className="absolute inset-0 overflow-hidden">
                      {panel.mobileImageSrc ? (
                        <span className="absolute inset-0 md:hidden">
                          <CmsImage
                            src={panel.mobileImageSrc}
                            alt={panel.mobileImageAlt || panel.imageAlt}
                            fill
                            priority={index === 0}
                            quality={90}
                            sizes="100vw"
                            className={cn(
                              'object-cover object-center transition-[filter,transform] duration-700',
                              '!left-1/2 !top-1/2 !right-auto !bottom-auto !h-auto !w-auto min-h-full min-w-full -translate-x-1/2 -translate-y-1/2 origin-center',
                              isActive
                                ? 'scale-105 grayscale-0'
                                : 'scale-110 grayscale group-hover:grayscale-[0.4]',
                            )}
                            fallbackSrc={MEDIA_PLACEHOLDER}
                          />
                        </span>
                      ) : null}
                      <span
                        className={cn(
                          'absolute inset-0',
                          panel.mobileImageSrc ? 'hidden md:block' : null,
                        )}
                      >
                        <CmsImage
                          src={panel.imageSrc}
                          alt={panel.imageAlt}
                          fill
                          priority={index === 0 && !panel.mobileImageSrc}
                          quality={90}
                          sizes={
                            isActive
                              ? '(min-width: 1280px) 800px, (min-width: 1024px) 55vw, 100vw'
                              : '80px'
                          }
                          className={cn(
                            'object-cover object-center transition-[filter,transform] duration-700',
                            'max-lg:!left-1/2 max-lg:!top-1/2 max-lg:!right-auto max-lg:!bottom-auto max-lg:!h-auto max-lg:!w-auto max-lg:min-h-full max-lg:min-w-full max-lg:-translate-x-1/2 max-lg:-translate-y-1/2 max-lg:origin-center',
                            isActive
                              ? 'scale-105 grayscale-0'
                              : 'scale-110 grayscale group-hover:grayscale-[0.4]',
                          )}
                          fallbackSrc={MEDIA_PLACEHOLDER}
                        />
                      </span>
                    </span>

                    <span
                      className={cn(
                        'absolute inset-0 transition-opacity duration-700',
                        isActive
                          ? 'bg-[linear-gradient(180deg,rgba(0,0,0,0.08)_0%,rgba(0,0,0,0.02)_38%,rgba(0,0,0,0.55)_72%,rgba(0,0,0,0.92)_100%)] opacity-100'
                          : 'bg-[linear-gradient(180deg,rgba(0,0,0,0.35)_0%,rgba(0,0,0,0.5)_45%,rgba(0,0,0,0.88)_100%)] opacity-100 group-hover:opacity-90',
                      )}
                    />
                    <span className="absolute inset-0 bg-black/20 transition-opacity duration-700 group-hover:bg-black/10" />

                    {/* Collapsed — compact tap row on mobile/tablet, vertical strip on desktop */}
                    <span
                      className={cn(
                        'absolute inset-0 z-10 flex transition-opacity duration-500 lg:flex-col lg:items-center lg:px-1',
                        isActive ? 'pointer-events-none opacity-0' : 'opacity-100',
                        'flex-row items-center justify-between gap-3 px-4 sm:px-5 lg:flex-col lg:justify-start lg:gap-0',
                      )}
                      aria-hidden={isActive}
                    >
                      <span className="shrink-0 text-[10px] font-medium tracking-[0.22em] text-white/50 lg:mt-6">
                        {formatPanelIndex(index, imagePanels.length)}
                      </span>
                      <span className="mb-8 mt-auto hidden max-h-[min(48vh,380px)] rotate-180 text-[10px] font-bold uppercase leading-none tracking-[0.24em] text-white/88 [writing-mode:vertical-rl] lg:block xl:mb-10 xl:text-[11px]">
                        {verticalPanelLabel(panel.title)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-right text-xs font-semibold uppercase tracking-[0.14em] text-white/85 sm:text-[13px] lg:hidden">
                        {panel.title}
                      </span>
                    </span>

                    {/* Active — copy inside panel */}
                    <span
                      className={cn(
                        'relative z-10 flex h-full w-full flex-col p-5 transition-all duration-500 sm:p-6 md:p-8 lg:p-9',
                        isActive ? 'opacity-100' : 'pointer-events-none opacity-0',
                      )}
                      aria-hidden={!isActive}
                    >
                      <span
                        className={cn(
                          'transition-all duration-500',
                          isActive ? 'block opacity-100' : 'hidden',
                        )}
                      >
                        <span className="block text-[11px] tracking-[0.2em] text-white/50 sm:text-xs">
                          {formatPanelIndex(index, imagePanels.length)}
                        </span>
                      </span>

                      <span className="mt-auto block transition-[writing-mode,opacity] duration-500 [writing-mode:horizontal-tb]">
                        <span
                          className={cn(
                            'block text-balance font-bold leading-tight text-white transition-all duration-500',
                            isActive
                              ? 'text-xl sm:text-2xl md:text-3xl lg:text-4xl xl:text-[2.85rem]'
                              : 'text-lg',
                          )}
                        >
                          {panel.title}
                        </span>
                        {renderPanelBody(panel, isActive)}
                      </span>
                    </span>

                    {isActive ? (
                      <span className="absolute inset-x-0 bottom-0 z-20 h-[3px] bg-white/10">
                        <span data-focus-progress className="block h-full w-0 bg-[#ed1c22]" />
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          </div>
        </Container>
      </div>
    </div>
  );
}
